import React, { useState, useRef, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Zap, ArrowLeft, Mail, KeyRound, Eye, EyeOff, RefreshCw, CheckCircle2, Lock } from 'lucide-react'
import { authAPI } from '../../services/api'

// ── OTP Input ──────────────────────────────────────────────
const OtpInput = ({ value, onChange, disabled }) => {
  const refs = useRef([])
  const digits = value.split('')

  const handleChange = (i, e) => {
    const val = e.target.value.replace(/[^a-zA-Z0-9]/g, '').slice(-1).toUpperCase()
    const next = digits.slice(); next[i] = val
    onChange(next.join(''))
    if (val && i < 5) refs.current[i + 1]?.focus()
  }
  const handleKey = (i, e) => {
    if (e.key === 'Backspace' && !digits[i] && i > 0) refs.current[i - 1]?.focus()
  }
  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/[^a-zA-Z0-9]/g, '').slice(0, 6).toUpperCase()
    if (pasted) { onChange(pasted.padEnd(6, '').slice(0, 6)); refs.current[Math.min(pasted.length, 5)]?.focus() }
    e.preventDefault()
  }

  return (
    <div style={{ display: 'flex', gap: 10, justifyContent: 'center', margin: '24px 0' }}>
      {Array.from({ length: 6 }).map((_, i) => (
        <input key={i} ref={el => refs.current[i] = el}
          type="text" inputMode="text" maxLength={1}
          value={digits[i] || ''}
          onChange={e => handleChange(i, e)}
          onKeyDown={e => handleKey(i, e)}
          onPaste={handlePaste}
          disabled={disabled}
          style={{
            width: 46, height: 54, textAlign: 'center', fontSize: 20, fontWeight: 800,
            border: `2px solid ${digits[i] ? '#7c3aed' : '#e2e2ef'}`,
            borderRadius: 10, outline: 'none', background: digits[i] ? '#f5f0ff' : '#fff',
            color: '#14142b', transition: 'all 0.15s', fontFamily: 'inherit',
            opacity: disabled ? 0.6 : 1,
          }}
        />
      ))}
    </div>
  )
}

// ── Main Component ────────────────────────────────────────
const ForgotPassword = () => {
  const navigate = useNavigate()
  const [step, setStep] = useState('email') // email | otp | password | done
  const [email, setEmail] = useState('')
  const [otp, setOtp] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [showPw, setShowPw] = useState(false)
  const [showCf, setShowCf] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [cooldown, setCooldown] = useState(0)

  useEffect(() => {
    if (cooldown <= 0) return
    const t = setTimeout(() => setCooldown(c => c - 1), 1000)
    return () => clearTimeout(t)
  }, [cooldown])

  const handleSendOTP = async (e) => {
    e?.preventDefault()
    setError('')
    if (!email.trim()) return setError('Email required')
    setLoading(true)
    try {
      await authAPI.forgotPassword(email.trim().toLowerCase())
      setStep('otp')
      setCooldown(30)
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send OTP. Try again.')
    } finally { setLoading(false) }
  }

  const handleResend = async () => {
    if (cooldown > 0) return
    setError('')
    setLoading(true)
    try {
      await authAPI.forgotPassword(email.trim().toLowerCase())
      setCooldown(30)
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to resend OTP.')
    } finally { setLoading(false) }
  }

  const handleOtpNext = (e) => {
    e.preventDefault()
    if (otp.length < 6) return setError('Enter all 6 digits')
    setError('')
    setStep('password')
  }

  const handleReset = async (e) => {
    e.preventDefault()
    setError('')
    if (password.length < 8) return setError('Password must be at least 8 characters')
    if (password !== confirm) return setError('Passwords do not match')
    setLoading(true)
    try {
      await authAPI.resetPassword({ email, otp, password, password_confirmation: confirm })
      setStep('done')
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.errors ? Object.values(err.response.data.errors || {})[0]?.[0] : 'Reset failed. Please try again.')
    } finally { setLoading(false) }
  }

  const leftCards = [
    { icon: '📧', title: 'Check your email', desc: 'OTP is sent to your registered email address' },
    { icon: '⏱️', title: 'OTP valid for 15 min', desc: 'Request a new OTP if it expires' },
    { icon: '🔒', title: 'Secure reset', desc: 'Your identity is verified via email OTP' },
  ]

  return (
    <>
      <style>{css}</style>
      <div className="fp-page">
        {/* LEFT */}
        <div className="fp-left">
          <div className="fp-left-inner">
            <Link to="/" className="fp-brand">
              <div className="fp-brand-icon"><Zap size={16} strokeWidth={2.5} /></div>
              <span>A1X<span className="fp-brand-accent">PAY</span></span>
            </Link>
            <div className="fp-left-content">
              <div className="fp-eyebrow">Password Recovery</div>
              <h2 className="fp-left-heading">Reset your password in 3 simple steps</h2>
              <p className="fp-left-sub">Enter your email, verify with OTP, and set a new password securely.</p>
              <div className="fp-steps">
                {['Enter email', 'Verify OTP', 'New password'].map((s, i) => (
                  <div key={i} className={`fp-step ${['email','otp','password','done'].indexOf(step) > i ? 'fp-step--done' : ['email','otp','password','done'].indexOf(step) === i ? 'fp-step--active' : ''}`}>
                    <div className="fp-step-num">{['email','otp','password','done'].indexOf(step) > i ? '✓' : i + 1}</div>
                    <div className="fp-step-label">{s}</div>
                  </div>
                ))}
              </div>
              <div className="fp-info-cards">
                {leftCards.map((c, i) => (
                  <div key={i} className="fp-info-card">
                    <div className="fp-info-icon">{c.icon}</div>
                    <div>
                      <div className="fp-info-title">{c.title}</div>
                      <div className="fp-info-desc">{c.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="fp-blob fp-blob-1" />
          <div className="fp-blob fp-blob-2" />
        </div>

        {/* RIGHT */}
        <div className="fp-right">
          <AnimatePresence mode="wait">

            {/* STEP 1 — EMAIL */}
            {step === 'email' && (
              <motion.div key="email" className="fp-card"
                initial={{ opacity: 0, x: 32 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -32 }}
                transition={{ duration: 0.3 }}>
                <div className="fp-icon-wrap"><Mail size={24} /></div>
                <h1 className="fp-title">Forgot your password?</h1>
                <p className="fp-sub">Enter your registered email and we'll send you a 6-digit OTP to reset your password.</p>
                <form onSubmit={handleSendOTP}>
                  <div className="fp-field">
                    <label className="fp-label">Email address</label>
                    <input type="email" className="fp-input" placeholder="you@example.com"
                      value={email} onChange={e => setEmail(e.target.value)} required autoFocus />
                  </div>
                  {error && <div className="fp-error">{error}</div>}
                  <button type="submit" className="fp-btn" disabled={loading}>
                    {loading ? 'Sending OTP…' : 'Send OTP'}
                  </button>
                </form>
                <Link to="/login" className="fp-back"><ArrowLeft size={14} /> Back to sign in</Link>
              </motion.div>
            )}

            {/* STEP 2 — OTP */}
            {step === 'otp' && (
              <motion.div key="otp" className="fp-card"
                initial={{ opacity: 0, x: 32 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -32 }}
                transition={{ duration: 0.3 }}>
                <div className="fp-icon-wrap" style={{ background: '#fff7ed', border: '1.5px solid #fed7aa', color: '#ea580c' }}>
                  <KeyRound size={24} />
                </div>
                <h1 className="fp-title">Check your email</h1>
                <p className="fp-sub">We've sent a 6-digit OTP to <strong>{email}</strong></p>
                <OtpInput value={otp} onChange={setOtp} disabled={loading} />
                {error && <div className="fp-error">{error}</div>}
                <button className="fp-btn" onClick={handleOtpNext} disabled={loading || otp.length < 6}>
                  Verify OTP
                </button>
                <button className="fp-resend" onClick={handleResend} disabled={cooldown > 0 || loading}>
                  <RefreshCw size={13} />
                  {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend OTP'}
                </button>
                <button className="fp-back" onClick={() => { setStep('email'); setOtp(''); setError('') }}>
                  <ArrowLeft size={14} /> Change email
                </button>
              </motion.div>
            )}

            {/* STEP 3 — NEW PASSWORD */}
            {step === 'password' && (
              <motion.div key="password" className="fp-card"
                initial={{ opacity: 0, x: 32 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -32 }}
                transition={{ duration: 0.3 }}>
                <div className="fp-icon-wrap" style={{ background: '#f0fdf4', border: '1.5px solid #bbf7d0', color: '#16a34a' }}>
                  <Lock size={24} />
                </div>
                <h1 className="fp-title">Set new password</h1>
                <p className="fp-sub">Choose a strong password with at least 8 characters.</p>
                <form onSubmit={handleReset}>
                  <div className="fp-field">
                    <label className="fp-label">New password</label>
                    <div className="fp-pw-wrap">
                      <input type={showPw ? 'text' : 'password'} className="fp-input fp-pw-input"
                        placeholder="Min 8 characters" value={password}
                        onChange={e => setPassword(e.target.value)} required autoFocus />
                      <button type="button" className="fp-pw-eye" onClick={() => setShowPw(v => !v)}>
                        {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>
                  <div className="fp-field">
                    <label className="fp-label">Confirm password</label>
                    <div className="fp-pw-wrap">
                      <input type={showCf ? 'text' : 'password'} className="fp-input fp-pw-input"
                        placeholder="Repeat password" value={confirm}
                        onChange={e => setConfirm(e.target.value)} required />
                      <button type="button" className="fp-pw-eye" onClick={() => setShowCf(v => !v)}>
                        {showCf ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>
                  {error && <div className="fp-error">{error}</div>}
                  <button type="submit" className="fp-btn" disabled={loading}>
                    {loading ? 'Resetting…' : 'Reset Password'}
                  </button>
                </form>
                <button className="fp-back" onClick={() => { setStep('otp'); setError('') }}>
                  <ArrowLeft size={14} /> Back
                </button>
              </motion.div>
            )}

            {/* DONE */}
            {step === 'done' && (
              <motion.div key="done" className="fp-card"
                initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.35 }} style={{ textAlign: 'center' }}>
                <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.15, type: 'spring', stiffness: 260 }}
                  style={{ width: 64, height: 64, margin: '0 auto 20px', background: '#f0fdf4', border: '1.5px solid #bbf7d0', borderRadius: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#16a34a' }}>
                  <CheckCircle2 size={30} />
                </motion.div>
                <h1 className="fp-title">Password reset!</h1>
                <p className="fp-sub">Your password has been reset successfully. You can now sign in with your new password.</p>
                <button className="fp-btn" onClick={() => navigate('/login')}>Go to Sign In</button>
              </motion.div>
            )}

          </AnimatePresence>
        </div>
      </div>
    </>
  )
}

const css = `
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

.fp-page {
  display: flex; min-height: 100vh;
  font-family: 'Plus Jakarta Sans', -apple-system, sans-serif;
}

.fp-left {
  display: none; flex: 0 0 34%; position: relative; overflow: hidden;
  background: linear-gradient(175deg, #7c3aed 0%, #5b21b6 55%, #3b0f8e 100%);
}
@media (min-width: 860px) { .fp-left { display: flex; flex-direction: column; } }

.fp-left-inner {
  position: relative; z-index: 2;
  display: flex; flex-direction: column; height: 100%; padding: 2rem 1.75rem;
}
.fp-brand {
  display: flex; align-items: center; gap: 0.5rem;
  text-decoration: none; font-size: 1.1rem; font-weight: 800; color: #fff; letter-spacing: -0.03em;
}
.fp-brand-icon {
  width: 1.875rem; height: 1.875rem; background: rgba(255,255,255,0.18);
  border: 1px solid rgba(255,255,255,0.25); border-radius: 0.4rem;
  display: flex; align-items: center; justify-content: center; color: #fff;
}
.fp-brand-accent { color: #ddd6fe; }
.fp-left-content { flex: 1; display: flex; flex-direction: column; justify-content: center; gap: 1.25rem; }
.fp-eyebrow {
  display: inline-block; padding: 0.25rem 0.7rem;
  background: rgba(255,255,255,0.12); border: 1px solid rgba(255,255,255,0.2);
  color: #e9d5ff; font-size: 0.7rem; font-weight: 600;
  letter-spacing: 0.08em; text-transform: uppercase; border-radius: 999px; width: fit-content;
}
.fp-left-heading { font-size: 1.3rem; font-weight: 800; color: #fff; line-height: 1.28; letter-spacing: -0.02em; margin: 0; text-wrap: balance; }
.fp-left-sub { color: rgba(255,255,255,0.6); font-size: 0.82rem; line-height: 1.6; margin: 0; }
.fp-steps { display: flex; flex-direction: column; gap: 0.5rem; }
.fp-step { display: flex; align-items: center; gap: 0.6rem; opacity: 0.45; transition: opacity 0.2s; }
.fp-step--active, .fp-step--done { opacity: 1; }
.fp-step-num {
  width: 24px; height: 24px; border-radius: 50%; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  font-size: 0.72rem; font-weight: 800;
  background: rgba(255,255,255,0.15); border: 1.5px solid rgba(255,255,255,0.3); color: #fff;
}
.fp-step--active .fp-step-num { background: #fff; color: #7c3aed; border-color: #fff; }
.fp-step--done .fp-step-num { background: #4ade80; border-color: #4ade80; color: #14532d; }
.fp-step-label { font-size: 0.82rem; font-weight: 600; color: #fff; }
.fp-info-cards { display: flex; flex-direction: column; gap: 0.6rem; }
.fp-info-card {
  display: flex; align-items: flex-start; gap: 0.6rem;
  background: rgba(255,255,255,0.07); border: 1px solid rgba(255,255,255,0.12);
  border-radius: 0.625rem; padding: 0.7rem 0.875rem;
}
.fp-info-icon { font-size: 1rem; flex-shrink: 0; margin-top: 1px; }
.fp-info-title { font-size: 0.8rem; font-weight: 700; color: #fff; margin-bottom: 2px; }
.fp-info-desc { font-size: 0.74rem; color: rgba(255,255,255,0.55); line-height: 1.4; }
.fp-blob { position: absolute; border-radius: 50%; filter: blur(70px); pointer-events: none; }
.fp-blob-1 { width: 300px; height: 300px; background: rgba(124,58,237,0.4); top: -80px; right: -80px; }
.fp-blob-2 { width: 220px; height: 220px; background: rgba(196,181,253,0.18); bottom: 40px; left: -50px; }

.fp-right {
  flex: 1; display: flex; align-items: center; justify-content: center;
  padding: 3rem 4rem; background: #f5f5fa;
}
@media (max-width: 859px) { .fp-right { padding: 2rem 1.25rem; min-height: 100vh; } }

.fp-card { width: 100%; max-width: 420px; }
.fp-icon-wrap {
  width: 56px; height: 56px; margin: 0 auto 20px;
  background: #f1ebff; border: 1.5px solid #ddd6fe;
  border-radius: 16px; display: flex; align-items: center; justify-content: center; color: #7c3aed;
}
.fp-title { font-size: 1.4rem; font-weight: 800; color: #14142b; letter-spacing: -0.03em; margin-bottom: 8px; text-align: center; }
.fp-sub { font-size: 0.88rem; color: #6e6e86; line-height: 1.6; margin-bottom: 22px; text-align: center; }
.fp-field { margin-bottom: 14px; }
.fp-label { display: block; font-size: 0.8rem; font-weight: 600; color: #3d3d5c; margin-bottom: 6px; }
.fp-input {
  width: 100%; padding: 10px 14px; font-size: 0.9rem; font-family: inherit;
  border: 1.5px solid #e2e2ef; border-radius: 10px; outline: none;
  background: #fff; color: #14142b; box-sizing: border-box; transition: border-color 0.15s;
}
.fp-input:focus { border-color: #7c3aed; box-shadow: 0 0 0 3px rgba(124,58,237,0.1); }
.fp-pw-wrap { position: relative; }
.fp-pw-input { padding-right: 40px; }
.fp-pw-eye {
  position: absolute; right: 12px; top: 50%; transform: translateY(-50%);
  background: none; border: none; cursor: pointer; color: #9090a8; padding: 2px;
}
.fp-pw-eye:hover { color: #7c3aed; }
.fp-error { background: #fef2f2; border: 1px solid #fecaca; color: #b91c1c; font-size: 0.82rem; padding: 9px 12px; border-radius: 8px; margin-bottom: 12px; }
.fp-btn {
  width: 100%; padding: 11px 20px; margin-bottom: 12px; border: none; cursor: pointer;
  font-size: 0.9rem; font-weight: 700; font-family: inherit;
  color: #fff; background: #7c3aed; border-radius: 10px;
  box-shadow: 0 4px 14px rgba(124,58,237,0.28); transition: background 0.2s, transform 0.15s;
}
.fp-btn:hover:not(:disabled) { background: #6d28d9; transform: translateY(-1px); }
.fp-btn:disabled { opacity: 0.6; cursor: not-allowed; transform: none; }
.fp-resend {
  display: flex; align-items: center; justify-content: center; gap: 6px;
  width: 100%; padding: 9px; margin-bottom: 10px;
  background: none; border: 1.5px solid #e2e2ef; border-radius: 10px;
  font-size: 0.84rem; font-weight: 600; color: #6e6e86; cursor: pointer; font-family: inherit;
  transition: border-color 0.15s, color 0.15s;
}
.fp-resend:not(:disabled):hover { border-color: #7c3aed; color: #7c3aed; }
.fp-resend:disabled { opacity: 0.5; cursor: not-allowed; }
.fp-back {
  display: flex; align-items: center; justify-content: center; gap: 5px;
  font-size: 0.85rem; font-weight: 500; color: #6e6e86; text-decoration: none;
  background: none; border: none; cursor: pointer; font-family: inherit; width: 100%;
  padding: 6px; transition: color 0.15s;
}
.fp-back:hover { color: #7c3aed; }
`

export default ForgotPassword
